import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { Footer } from "@/components/Footer";
import { ParallaxBanner } from "@/components/ParallaxBanner";
import { SEO } from "@/components/SEO";
import { OtisCaseStudy } from "@/components/OtisCaseStudy";
import { KashaCaseStudy } from "@/components/KashaCaseStudy";
import { AlekbgCaseStudy } from "@/components/AlekbgCaseStudy";
import { OpenTagCaseStudy } from "@/components/OpenTagCaseStudy";
import { WellmaxiCaseStudy } from "@/components/WellmaxiCaseStudy";
import { CaseStudyMedia, CaseStudyReveal } from "@/components/CaseStudyMotion";
import { homeCards, projects } from "@/data/projects";
import { useLanguage } from "@/contexts/LanguageContext";

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { t, language } = useLanguage();
  const [activeSection, setActiveSection] = useState("");
  const sectionsRef = useRef<{ id: string; label: string }[]>([]);
  
  // Look the case study up by its own id — single source of truth in src/data/projects.ts
  const publicCaseStudyIds = homeCards.flatMap((card) => card.caseStudyId ? [card.caseStudyId] : []);
  const projectIndex = id ? publicCaseStudyIds.indexOf(id) : -1;
  const project = projectIndex >= 0 ? projects.find((item) => item.id === id) ?? null : null;

  // Track active section with optimized scroll handling
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const sectionElements = sectionsRef.current.map(section => ({
            id: section.id,
            element: document.getElementById(section.id)
          }));

          for (let i = sectionElements.length - 1; i >= 0; i--) {
            const section = sectionElements[i];
            if (section.element) {
              const rect = section.element.getBoundingClientRect();
              if (rect.top <= 200) {
                setActiveSection(section.id);
                break;
              }
            }
          }
          
          ticking = false;
        });

        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!project) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold">{t("pd.notFound")}</h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("pd.backHome")}
          </Link>
        </div>
      </div>
    );
  }

  // Localized fields
  const title = language === "bg" ? project.title_bg : project.title;
  const role = language === "bg" ? project.role_bg : project.role;
  const context = language === "bg" ? project.context_bg : project.context;
  const challenge = language === "bg" ? project.challenge_bg : project.challenge;
  const aiElement = language === "bg" ? project.aiElement_bg : project.aiElement;
  const myRole = language === "bg" ? project.myRole_bg : project.myRole;
  const process = language === "bg" ? project.process_bg : project.process;
  const reflection = language === "bg" ? project.reflection_bg : project.reflection;
  const outcome = {
    metrics: language === "bg" ? project.outcome.metrics_bg : project.outcome.metrics,
    learning: language === "bg" ? project.outcome.learning_bg : project.outcome.learning,
    impact: language === "bg" ? project.outcome.impact_bg : project.outcome.impact,
  };
  const isOtis = project.id === "otis-ai-marketing";
  const isKashaFlagship = project.id === "kasha-ai-personalization";
  const isAlekbg = project.id === "alekbg-commerce-brand";
  const isOpenTag = project.id === "opentag-loyalty-system";
  const isWellmaxi = project.id === "wellmaxi-brand-catalogue";

  const genericSections = [
    { id: "background", label: t("pd.section.background"), show: Boolean(context) },
    { id: "challenge", label: t("pd.section.challenge"), show: Boolean(challenge) },
    { id: "research", label: t("pd.section.research"), show: process.length > 0 },
    { id: "solution", label: t("pd.section.solution"), show: Boolean(aiElement) },
    { id: "implementation", label: t("pd.section.implementation"), show: process.length > 2 },
    { id: "role", label: t("pd.section.role"), show: myRole.length > 0 },
    { id: "impact", label: t("pd.section.impact"), show: Boolean(outcome.impact || outcome.metrics) },
    { id: "lessons", label: t("pd.section.lessons"), show: Boolean(outcome.learning) },
  ].filter((s) => s.show);
  const otisSectionLabels = language === "bg"
    ? [
        { id: "overview", label: "Преглед" },
        { id: "challenge", label: "Предизвикателство" },
        { id: "research", label: "Проучване" },
        { id: "direction", label: "UX посока" },
        { id: "solution", label: "Решение" },
        { id: "recommendations", label: "Препоръки" },
        { id: "mobile", label: "Мобилно" },
        { id: "system", label: "Система" },
        { id: "role", label: "Моята роля" },
      ]
    : [
        { id: "overview", label: "Overview" },
        { id: "challenge", label: "Challenge" },
        { id: "research", label: "Research" },
        { id: "direction", label: "UX direction" },
        { id: "solution", label: "Solution" },
        { id: "recommendations", label: "Recommendations" },
        { id: "mobile", label: "Mobile" },
        { id: "system", label: "System" },
        { id: "role", label: "My role" },
      ];
  const kashaSectionLabels = language === "bg"
    ? [
        { id: "overview", label: "Преглед" },
        { id: "context", label: "Контекст" },
        { id: "onboarding", label: "Onboarding" },
        { id: "workflows", label: "Потоци" },
        { id: "builder", label: "AI инструменти" },
        { id: "account", label: "Профил" },
        { id: "operations", label: "Операции" },
        { id: "system", label: "Система" },
        { id: "role", label: "Моята роля" },
        { id: "learning", label: "Научено" },
      ]
    : [
        { id: "overview", label: "Overview" },
        { id: "context", label: "Context" },
        { id: "onboarding", label: "Onboarding" },
        { id: "workflows", label: "Workflows" },
        { id: "builder", label: "AI tooling" },
        { id: "account", label: "Account UX" },
        { id: "operations", label: "Operations" },
        { id: "system", label: "System" },
        { id: "role", label: "My role" },
        { id: "learning", label: "Learnings" },
      ];
  const alekbgSectionLabels = language === "bg"
    ? [
        { id: "overview", label: "Преглед" }, { id: "context", label: "Контекст" },
        { id: "brand", label: "Бранд" }, { id: "commerce", label: "B2C" },
        { id: "decision", label: "Избор" }, { id: "wholesale", label: "B2B" },
        { id: "international", label: "Езици" }, { id: "content", label: "Съдържание" },
        { id: "shipped", label: "Реализация" }, { id: "role", label: "Моята роля" },
        { id: "learning", label: "Научено" },
      ]
    : [
        { id: "overview", label: "Overview" }, { id: "context", label: "Context" },
        { id: "brand", label: "Brand" }, { id: "commerce", label: "B2C" },
        { id: "decision", label: "Decision UX" }, { id: "wholesale", label: "B2B" },
        { id: "international", label: "Languages" }, { id: "content", label: "Content" },
        { id: "shipped", label: "Shipped" }, { id: "role", label: "My role" },
        { id: "learning", label: "Learnings" },
      ];
  const openTagSectionLabels = language === "bg"
    ? [{ id: "overview", label: "Преглед" }, { id: "context", label: "Контекст" }, { id: "research", label: "Проучване" }, { id: "goals", label: "Цели" }, { id: "operator", label: "Оператор" }, { id: "player", label: "Играч" }, { id: "iteration", label: "Итерации" }, { id: "current", label: "Днес" }, { id: "role", label: "Моята роля" }, { id: "learning", label: "Научено" }]
    : [{ id: "overview", label: "Overview" }, { id: "context", label: "Context" }, { id: "research", label: "Research" }, { id: "goals", label: "Goals" }, { id: "operator", label: "Operator" }, { id: "player", label: "Player" }, { id: "iteration", label: "Iteration" }, { id: "current", label: "Today" }, { id: "role", label: "My role" }, { id: "learning", label: "Learnings" }];
  const wellmaxiSectionLabels = language === "bg"
    ? [{ id: "overview", label: "Преглед" }, { id: "context", label: "Контекст" }, { id: "brand", label: "Бранд" }, { id: "homepage", label: "Начало" }, { id: "catalogue", label: "Каталог" }, { id: "trust", label: "Доверие" }, { id: "system", label: "Система" }, { id: "role", label: "Моят принос" }, { id: "learning", label: "Научено" }]
    : [{ id: "overview", label: "Overview" }, { id: "context", label: "Context" }, { id: "brand", label: "Brand" }, { id: "homepage", label: "Homepage" }, { id: "catalogue", label: "Catalogue" }, { id: "trust", label: "Trust" }, { id: "system", label: "System" }, { id: "role", label: "My contribution" }, { id: "learning", label: "Learnings" }];
  const sections = isOtis ? otisSectionLabels : isKashaFlagship ? kashaSectionLabels : isAlekbg ? alekbgSectionLabels : isOpenTag ? openTagSectionLabels : isWellmaxi ? wellmaxiSectionLabels : genericSections;
  sectionsRef.current = sections;
  const H2 = "text-2xl sm:text-3xl md:text-5xl font-bold mb-4 md:mb-6";
  const BODY = "text-base md:text-lg text-muted-foreground max-w-3xl leading-relaxed";

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: reduceMotion ? 'auto' : 'smooth'
      });
    }
  };

  return (
    <>
      <SEO
        title={`${project.title} | ${project.company} Case Study — Khaled Kasmou`}
        description={`${project.context} A UX/UI case study by Khaled Kasmou (${project.role} at ${project.company}).`}
        path={`/project/${id}`}
        type="article"
        image={`https://khaledkasmou.me${project.heroImage}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CreativeWork",
            name: project.title,
            headline: project.title,
            description: project.context,
            url: `https://khaledkasmou.me/project/${id}`,
            image: `https://khaledkasmou.me${project.heroImage}`,
            keywords: project.tags.join(", "),
            author: {
              "@type": "Person",
              name: "Khaled Kasmou",
              url: "https://khaledkasmou.me/",
            },
            creator: {
              "@type": "Person",
              name: "Khaled Kasmou",
            },
            about: project.aiElement,
            sourceOrganization: {
              "@type": "Organization",
              name: project.company,
            },
            inLanguage: "en",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://khaledkasmou.me/" },
              { "@type": "ListItem", position: 2, name: "Projects", item: "https://khaledkasmou.me/#projects" },
              { "@type": "ListItem", position: 3, name: project.title, item: `https://khaledkasmou.me/project/${id}` },
            ],
          },
        ]}
      />
      {/* Mobile section nav — horizontal, sticky */}
      {sections.length > 1 && (
        <nav className="lg:hidden sticky top-16 z-40 bg-background/95 backdrop-blur-sm border-b border-border overflow-x-auto" aria-label="Case study sections">
          <div className="flex gap-3 px-4 py-3 w-max">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                aria-current={activeSection === section.id ? 'location' : undefined}
                className={`focus-premium text-xs font-medium px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
                  activeSection === section.id ? 'bg-foreground text-background border-foreground' : 'border-muted-foreground/50 text-muted-foreground hover:text-foreground hover:border-foreground/60'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </nav>
      )}
      {/* Progress Navigation Sidebar */}
      <div className="fixed left-6 top-1/2 -translate-y-1/2 z-50 hidden lg:block">
        <nav className="space-y-3" aria-label="Case study progress">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => scrollToSection(section.id)}
              aria-current={activeSection === section.id ? 'location' : undefined}
              aria-label={section.label}
              className="focus-premium group flex items-center gap-3 transition-all duration-300"
            >
              <div className={`h-8 w-1 rounded-full transition-all duration-300 ${
                activeSection === section.id 
                  ? 'bg-blob w-2 shadow-lg shadow-blob/50' 
                  : 'bg-muted-foreground/60 group-hover:bg-muted-foreground'
              }`} />
              <span className={`text-xs font-medium transition-all duration-300 whitespace-nowrap ${
                activeSection === section.id 
                  ? 'text-foreground translate-x-0 opacity-100' 
                  : 'text-muted-foreground -translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0'
              }`}>
                {section.label}
              </span>
            </button>
          ))}
        </nav>
      </div>

      <main className="min-h-screen pb-24">
        {/* Back button */}
        <div className="pt-24 max-w-6xl mx-auto px-6 mb-8">
          <Link
            to="/"
            className="focus-premium inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            {t("pd.back")}
          </Link>
        </div>

        {/* Status Badge & Title Section */}
        <section className="max-w-7xl mx-auto px-6 mb-10 md:mb-16">
          <div className="mb-8">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blob/20 text-blob-foreground text-xs font-semibold uppercase tracking-wide">
              <span className="w-1.5 h-1.5 rounded-full bg-blob animate-pulse" />
              {t("pd.shipped")}
            </span>
          </div>

          {/* Two Column Layout: Title Left, Subtitle Right */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-12 items-start mb-10 md:mb-16">
            {/* Title - Left Column */}
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {title}
              </h1>
            </div>

            {/* Subtitle - Right Column */}
            <div className="md:pt-4">
              <p className="text-base sm:text-lg md:text-2xl text-muted-foreground leading-relaxed">
                {context}
              </p>
            </div>
          </div>

          {/* Meta Info Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 pt-8 border-t border-border">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">{t("pd.meta.product")}</div>
              <div className="font-medium">{project.company}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">{t("pd.meta.skills")}</div>
              <div className="flex flex-wrap gap-2">
                {project.tags.slice(0, 2).map((tag, i) => (
                  <span key={i} className="text-sm">{tag}</span>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">{t("pd.meta.role")}</div>
              <div className="font-medium">{role}</div>
            </div>
            {project.timeline && (
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">{t("pd.meta.timeline")}</div>
                <div className="font-medium">{project.timeline}</div>
              </div>
            )}
          </div>
        </section>

        {!isOtis && !isKashaFlagship && !isAlekbg && !isOpenTag && !isWellmaxi && (
          <section className="mb-16 md:mb-24 overflow-hidden">
            <ParallaxBanner image={project.heroImage} title="" height="50vh" />
          </section>
        )}

        {isOtis ? (
          <OtisCaseStudy
            language={language}
            context={context}
            challenge={challenge}
            myRole={myRole}
            learning={outcome.learning}
            reflection={reflection}
          />
        ) : isKashaFlagship ? (
          <KashaCaseStudy language={language} />
        ) : isAlekbg ? (
          <AlekbgCaseStudy language={language} />
        ) : isOpenTag ? (
          <OpenTagCaseStudy language={language} />
        ) : isWellmaxi ? (
          <WellmaxiCaseStudy language={language} />
        ) : <>
        <div className="max-w-7xl mx-auto px-6 space-y-16 md:space-y-24">
          {/* Background */}
          {context && (
<CaseStudyReveal><section id="background" className="space-y-6 scroll-mt-24">
            <h2 className={H2}>{t("pd.section.background")}</h2>
            <p className={BODY}>
              {context}
            </p>
          </section></CaseStudyReveal>
          )}

          {/* Challenge Section */}
          {challenge && (
<CaseStudyReveal direction="left"><section id="challenge" className="space-y-6 scroll-mt-24">
            <h2 className={H2}>{t("pd.section.challenge")}</h2>
            <p className={BODY}>
              {challenge}
            </p>
          </section></CaseStudyReveal>
          )}

          {/* Research */}
          {process.length > 0 && (
<CaseStudyReveal><section id="research" className="space-y-6 scroll-mt-24">
            <h2 className={H2}>{t("pd.section.research")}</h2>
            <div className="space-y-4">
              {process.slice(0, 2).map((item, index) => (
                <p key={index} className={BODY}>
                  {item}
                </p>
              ))}
            </div>
          </section></CaseStudyReveal>
          )}

          {/* Solution */}
          {aiElement && (
<CaseStudyReveal direction="right"><section id="solution" className="space-y-6 scroll-mt-24">
            <h2 className={H2}>{t("pd.section.solution")}</h2>
            <p className={BODY}>
              {aiElement}
            </p>
          </section></CaseStudyReveal>
          )}

          {/* Implementation — remaining process steps, numbered */}
          {process.length > 2 && (
<CaseStudyReveal><section id="implementation" className="space-y-6 scroll-mt-24">
            <h2 className={H2}>{t("pd.section.implementation")}</h2>
            <div className="space-y-4">
              {process.slice(2).map((step, index) => (
                <div key={index} className="flex gap-4">
                  <span className="text-readable-accent font-semibold min-w-[2rem]">{String(index + 1).padStart(2, '0')}</span>
                  <p className={BODY}>
                    {step}
                  </p>
                </div>
              ))}
            </div>
          </section></CaseStudyReveal>
          )}

          {/* My Role */}
          {myRole.length > 0 && (
<CaseStudyReveal direction="left"><section id="role" className="space-y-6 scroll-mt-24">
            <h2 className={H2}>{t("pd.section.role")}</h2>
            <ul className="space-y-3">
              {myRole.map((item, index) => (
                <li key={index} className="text-lg text-muted-foreground max-w-3xl leading-relaxed flex gap-3">
                  <span className="text-readable-accent">•</span>
                  {item}
                </li>
              ))}
            </ul>
          </section></CaseStudyReveal>
          )}
        </div>

        {/* Visuals — only real, distinct artefacts */}
        {project.mockupImage && (
          <section className="max-w-7xl mx-auto px-6 mt-16 md:mt-24 grid md:grid-cols-2 gap-6">
            <CaseStudyReveal direction="left"><CaseStudyMedia><img
              src={project.heroImage}
              alt={`${title} — interface design by Khaled Kasmou`}
              loading="lazy"
              className="w-full h-full object-cover rounded-2xl"
            /></CaseStudyMedia></CaseStudyReveal>
            <CaseStudyReveal direction="right" delay={1}><CaseStudyMedia><img
              src={project.mockupImage}
              alt={`${title} — mobile screens by Khaled Kasmou`}
              loading="lazy"
              className="w-full h-full object-cover rounded-2xl"
            /></CaseStudyMedia></CaseStudyReveal>
          </section>
        )}

        <div className="max-w-7xl mx-auto px-6 space-y-16 md:space-y-24 mt-24">
          {/* Impact */}
          {(outcome.impact || outcome.metrics) && (
<CaseStudyReveal><section id="impact" className="space-y-6 scroll-mt-24">
            <h2 className={H2}>{t("pd.section.impact")}</h2>
            <p className={BODY}>
              {outcome.impact}
            </p>
            {outcome.metrics && <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-8">
              {outcome.metrics.split(',').map((metric, index) => (
                <div key={index} className="space-y-2">
                  <div className="w-8 h-8 rounded bg-blob/20 flex items-center justify-center text-sm font-semibold text-blob-foreground">
                    {String(index + 1).padStart(2, '0')}
                  </div>
                  <div className="text-lg text-muted-foreground">
                    {metric.trim()}
                  </div>
                </div>
              ))}
            </div>}
          </section></CaseStudyReveal>
          )}

          {/* Lessons Learned */}
          {outcome.learning && (
<CaseStudyReveal direction="right"><section id="lessons" className="space-y-6 scroll-mt-24">
            <h2 className={H2}>{t("pd.section.lessons")}</h2>
            <div className="bg-blob/10 border-l-4 border-blob p-6 rounded-r-lg max-w-3xl">
              <p className="text-lg leading-relaxed">
                {outcome.learning}
              </p>
            </div>
          </section></CaseStudyReveal>
          )}

          {/* Reflection */}
          {reflection && (
            <section className="border-t border-border pt-12 space-y-6">
              <h3 className="text-xl font-bold text-muted-foreground mb-6">{t("pd.reflection")}</h3>
              <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed italic">
                "{reflection}"
              </p>
            </section>
          )}

          {/* Request Case Study CTA */}
          <section className="bg-gradient-to-br from-blob/10 to-blob/5 p-8 md:p-12 rounded-2xl text-center space-y-6">
            <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold">{t("pd.requestCS.title")}</h3>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t("pd.requestCS.body")}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 bg-foreground text-background rounded-full font-semibold hover:opacity-90 transition-opacity"
            >
              {t("pd.requestCS.cta")}
              <ExternalLink className="w-4 h-4" />
            </Link>
          </section>

        </div>
        </>}

          {/* Next Project */}
        <div className="max-w-7xl mx-auto px-6 mt-20 md:mt-32">
          {(() => {
            const publicSequence = homeCards
              .map((card) => projects.find((item) => item.id === card.caseStudyId))
              .filter((item): item is (typeof projects)[number] => Boolean(item));
            const sequenceIndex = publicSequence.findIndex((item) => item.id === project.id);
            const next = (sequenceIndex >= 0
              ? publicSequence[(sequenceIndex + 1) % publicSequence.length]
              : projects.find((item) => item.id === publicCaseStudyIds[(projectIndex + 1) % publicCaseStudyIds.length])) ?? project;
            const nextTitle = language === "bg" ? next.title_bg : next.title;
            return (
              <section className="pt-12 border-t border-border">
                <h3 className="text-sm text-muted-foreground uppercase tracking-wide mb-6">{t("pd.next")}</h3>
                <Link to={`/project/${next.id}`} className="focus-premium group block">
                  <div className="relative aspect-video bg-muted rounded-lg overflow-hidden mb-4">
                    <img
                      src={next.heroImage}
                      alt={`${nextTitle} — next case study by Khaled Kasmou`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-6">
                    <h4 className="text-xl md:text-2xl font-bold group-hover:text-readable-accent transition-colors">
                      {nextTitle}
                    </h4>
                    <ArrowLeft className="w-6 h-6 shrink-0 rotate-180 group-hover:translate-x-2 transition-transform" />
                  </div>
                </Link>
              </section>
            );
          })()}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default ProjectDetail;
